import React from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { LayoutDashboard, Users, GitBranch, ShieldCheck, FileBarChart2, Wallet, Cpu, LifeBuoy, Network, Megaphone, LinkIcon, Image, LogOut, Search, BellDot, Inbox, MessageSquareText, MessageSquare, MessagesSquare, Handshake, Activity, Lock, FileText, Newspaper, Package, Webhook, BarChart3, Layout, ScanSearch, Store, Briefcase, RotateCw, Mails, Eraser, Phone, TrendingUp, PenSquare, Shuffle, Settings as SettingsIcon } from "lucide-react";
import WelcomeTour from "@/components/dashboard/WelcomeTour";
import OnboardingTour from "@/components/dashboard/OnboardingTour";
import VerifyEmailBanner from "@/components/dashboard/VerifyEmailBanner";
import UnsignedContractBanner from "@/components/dashboard/UnsignedContractBanner";
import FeedbackWidget from "@/components/dashboard/FeedbackWidget";
import SupportChatWidget from "@/components/SupportChatWidget";
import { MobileMenuButton, MobileNavDrawer, MobileBottomNav } from "@/components/dashboard/MobileNav";
import QrPhoneButton from "@/components/dashboard/QrPhoneButton";

const NAVS = {
  client: [
    { to: "/dashboard/orders", label: "Orders", Icon: Package },
    { to: "/dashboard/campaign", label: "Campaign", Icon: Megaphone },
    { to: "/dashboard/leads", label: "Leads", Icon: Users },
    { to: "/dashboard/delivery", label: "Delivery", Icon: Webhook },
    { to: "/dashboard/compliance", label: "Compliance", Icon: ShieldCheck },
    { to: "/dashboard", label: "Overview", Icon: LayoutDashboard, end: true },
    { to: "/dashboard/billing", label: "Billing", Icon: Wallet },
    { to: "/dashboard/ai-systems", label: "AI Add-ons", Icon: Cpu },
    { to: "/dashboard/live-calls", label: "Live Calls", Icon: Phone },
    { to: "/dashboard/auto-followup", label: "Auto Follow-Up", Icon: MessagesSquare },
    { to: "/dashboard/replies", label: "Replies inbox", Icon: Inbox },
    { to: "/dashboard/addons", label: "All add-ons", Icon: Package },
    { to: "/dashboard/contracts", label: "Contracts", Icon: FileText },
    { to: "/dashboard/support", label: "Support", Icon: LifeBuoy },
    { to: "/dashboard/my-feedback", label: "My Feedback", Icon: MessageSquareText },
    { to: "/dashboard/settings", label: "Settings", Icon: SettingsIcon, group: "footer" },
  ],
  admin: [
    { to: "/admin", label: "Overview", Icon: LayoutDashboard, end: true },
    { to: "/admin/routing", label: "Ping Trees", Icon: GitBranch },
    { to: "/admin/buyers", label: "Buyers", Icon: Network },
    { to: "/admin/campaigns", label: "Campaigns", Icon: Megaphone },
    { to: "/admin/replay", label: "Lead Replay", Icon: RotateCw },
    { to: "/admin/invoices", label: "Invoices", Icon: FileText },
    { to: "/admin/contracts", label: "Contracts", Icon: Lock },
    { to: "/admin/compliance", label: "Compliance", Icon: ShieldCheck },
    { to: "/admin/compliance-center", label: "Command Centre", Icon: ScanSearch },
    { to: "/admin/users", label: "Users", Icon: Users },
    { to: "/admin/publishers", label: "Publishers", Icon: Newspaper },
    { to: "/admin/partners", label: "Partners", Icon: Handshake },
    { to: "/admin/feedback", label: "Feedback", Icon: Inbox },
    { to: "/admin/enquiries", label: "Enquiries", Icon: Mails, badge: "enquiries" },
    { to: "/admin/sector-demand", label: "Sector Demand", Icon: TrendingUp },
    { to: "/admin/content", label: "Insights", Icon: PenSquare },
    { to: "/admin/careers", label: "Careers", Icon: Briefcase },
    { to: "/admin/support", label: "Support", Icon: MessageSquare, badge: "support" },
    { to: "/admin/audit", label: "Audit Log", Icon: Activity },
    { to: "/admin/cleanup", label: "Data Cleanup", Icon: Eraser },
    { to: "/admin/security", label: "Security", Icon: Lock },
    { to: "/admin/settings", label: "Settings", Icon: SettingsIcon, group: "footer" },
  ],
  affiliate: [
    { to: "/affiliate", label: "Overview", Icon: LayoutDashboard, end: true },
    { to: "/affiliate/links", label: "Tracking Links", Icon: LinkIcon },
    { to: "/affiliate/landers", label: "Landers", Icon: Layout },
    { to: "/affiliate/analytics", label: "Analytics", Icon: BarChart3 },
    { to: "/affiliate/commissions", label: "Commissions", Icon: FileBarChart2 },
    { to: "/affiliate/creatives", label: "Creatives", Icon: Image },
    { to: "/affiliate/contracts", label: "Contracts", Icon: FileText },
    { to: "/affiliate/my-feedback", label: "My Feedback", Icon: MessageSquareText },
    { to: "/affiliate/settings", label: "Settings", Icon: SettingsIcon, group: "footer" },
  ],
};

const ROLE_LABEL = { client: "Client Portal", admin: "Admin Control Plane", affiliate: "Affiliate Portal" };

// Mobile bottom-nav: the 5 most-used routes per role.
// Order = display order left-to-right.
const BOTTOM_NAV = {
  client: [
    { to: "/dashboard",          label: "Overview", Icon: LayoutDashboard, end: true },
    { to: "/dashboard/leads",    label: "Leads",    Icon: Users },
    { to: "/dashboard/replies",  label: "Replies",  Icon: Inbox },
    { to: "/dashboard/billing",  label: "Billing",  Icon: Wallet },
    { to: "/dashboard/support",  label: "Support",  Icon: LifeBuoy },
  ],
  admin: [
    { to: "/admin",                     label: "Overview", Icon: LayoutDashboard, end: true },
    { to: "/admin/buyers",              label: "Buyers",   Icon: Network },
    { to: "/admin/compliance-center",   label: "Centre",   Icon: ScanSearch },
    { to: "/admin/support",             label: "Support",  Icon: MessageSquare, badge: "support" },
    { to: "/admin/settings",            label: "Settings", Icon: SettingsIcon },
  ],
  affiliate: [
    { to: "/affiliate",             label: "Overview",    Icon: LayoutDashboard, end: true },
    { to: "/affiliate/links",       label: "Links",       Icon: LinkIcon },
    { to: "/affiliate/analytics",   label: "Analytics",   Icon: BarChart3 },
    { to: "/affiliate/commissions", label: "Commissions", Icon: FileBarChart2 },
    { to: "/affiliate/settings",    label: "Settings",    Icon: SettingsIcon },
  ],
};

export default function DashboardLayout({ role }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const items = NAVS[role] || [];
  const bottomItems = BOTTOM_NAV[role] || [];
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);
  const handleLogout = React.useCallback(() => { logout(); nav("/"); }, [logout, nav]);
  // iter64: admin-only support unread polling (cheap, 30s)
  const [supportUnread, setSupportUnread] = React.useState(0);
  const [enquiriesUnread, setEnquiriesUnread] = React.useState(0);
  React.useEffect(() => {
    if (role !== "admin") return;
    let alive = true;
    const tick = async () => {
      try {
        const headers = { Authorization: `Bearer ${localStorage.getItem("connexis_token")}` };
        const base = process.env.REACT_APP_BACKEND_URL || "";
        const [r, e] = await Promise.all([
          fetch(`${base}/api/admin/support/unread`, { headers }),
          fetch(`${base}/api/admin/enquiries/unread`, { headers }),
        ]);
        if (r.ok) { const d = await r.json(); if (alive) setSupportUnread(d.unread_messages || 0); }
        if (e.ok) { const d = await e.json(); if (alive) setEnquiriesUnread(d.unread || 0); }
      } catch { /* noop */ }
    };
    tick();
    const id = setInterval(tick, 30_000);
    return () => { alive = false; clearInterval(id); };
  }, [role]);
  return (
    <div className="min-h-screen bg-slate-50/40">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 shrink-0 border-r border-slate-200 bg-white min-h-screen sticky top-0 hidden lg:flex flex-col" data-testid="dash-sidebar">
          <Link to="/" className="flex items-center gap-2.5 px-5 h-16 border-b border-slate-200">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-slate-900 text-white">
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 7h7M4 12h12M4 17h7" strokeLinecap="round" /><circle cx="18" cy="7" r="2.2" fill="#0055FF" stroke="none" /><circle cx="18" cy="17" r="2.2" fill="#0055FF" stroke="none" /></svg>
            </span>
            <div className="leading-tight">
              <div className="text-[14px] font-semibold tracking-tight text-slate-900">Connexis<span className="text-primary">.</span></div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">{ROLE_LABEL[role]}</div>
            </div>
          </Link>
          <nav className="p-3 space-y-0.5 flex-1">
            {items.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end}
                className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <n.Icon className="size-4" strokeWidth={1.7} />
                <span className="flex-1">{n.label}</span>
                {n.badge === "support" && supportUnread > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold" data-testid="nav-support-badge">{supportUnread > 9 ? "9+" : supportUnread}</span>
                )}
                {n.badge === "enquiries" && enquiriesUnread > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#0055FF] text-white text-[10px] font-semibold" data-testid="nav-enquiries-badge">{enquiriesUnread > 9 ? "9+" : enquiriesUnread}</span>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="p-3 border-t border-slate-200">
            <div className="flex items-center gap-2.5 px-2 py-2">
              <Avatar className="size-8"><AvatarFallback className="bg-slate-900 text-white text-xs">{(user?.name || "??").split(" ").map(s => s[0]).slice(0, 2).join("")}</AvatarFallback></Avatar>
              <div className="leading-tight min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">{user?.name}</div>
                <div className="text-[11px] text-slate-500 truncate">{user?.company || user?.email}</div>
              </div>
            </div>
            {role === "admin" && (
              <Button variant="ghost" size="sm" asChild className="w-full justify-start mt-1" data-testid="admin-switch-area">
                <Link to="/choose?pick=1"><Shuffle className="size-4" /> Switch area</Link>
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => { logout(); nav("/"); }} className="w-full justify-start mt-1" data-testid="logout-btn"><LogOut className="size-4" /> Sign out</Button>
          </div>
        </aside>

        <div className="flex-1 min-w-0">
          <VerifyEmailBanner />
          <div className="sticky top-0 z-30 bg-white/85 backdrop-blur-xl border-b border-slate-200">
            <div className="flex items-center justify-between h-16 px-5 lg:px-8 gap-2">
              <MobileMenuButton onClick={() => setMobileNavOpen(true)} />
              <button
                type="button"
                onClick={() => {
                  const ev = new KeyboardEvent("keydown", { key: "k", metaKey: true, ctrlKey: true, bubbles: true });
                  document.dispatchEvent(ev);
                }}
                className="flex items-center gap-3 flex-1 max-w-md text-left hover:bg-slate-50 rounded-md px-1 -mx-1 transition-colors"
                data-testid="dash-search"
              >
                <Search className="size-4 text-slate-400" />
                <span className="flex-1 text-sm text-slate-400 truncate">Search…</span>
                <kbd className="hidden sm:inline-flex items-center rounded border border-slate-200 px-1.5 py-0.5 text-[10px] font-mono text-slate-500">⌘K</kbd>
              </button>
              <div className="flex items-center gap-3">
                <span className="hidden md:inline-flex items-center gap-1.5 text-xs text-slate-500"><span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" /> Routing healthy</span>
                <QrPhoneButton />
                <Button variant="ghost" size="icon" className="text-slate-600" data-testid="bell-btn"><BellDot className="size-4" /></Button>
              </div>
            </div>
          </div>
          <main className="p-5 lg:p-8 pb-40 lg:pb-36 space-y-5">
            <UnsignedContractBanner />
            <Outlet />
          </main>
        </div>
      </div>
      {/* Mobile-only nav surfaces */}
      <MobileNavDrawer
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        items={items}
        roleLabel={ROLE_LABEL[role]}
        user={user}
        onLogout={handleLogout}
        badges={{ support: supportUnread, enquiries: enquiriesUnread }}
      />
      <MobileBottomNav items={bottomItems} badges={{ support: supportUnread, enquiries: enquiriesUnread }} />
      {role === "client" && <WelcomeTour user={user} />}
      <OnboardingTour role={role} />
      <FeedbackWidget />
      <SupportChatWidget />
    </div>
  );
}
